import React, { useState } from 'react';
import {
  Box,
  Card,
  CardMedia,
  IconButton,
  Typography,
  useTheme,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Close,
  PhotoLibrary,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

interface ImageCarouselProps {
  images: string[];
  height?: number;
  showThumbnails?: boolean;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  height = 400,
  showThumbnails = true,
}) => {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index: number) => {
    setCurrentIndex(index);
  };

  if (!images || images.length === 0) {
    return (
      <Card sx={{ height }}>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.grey[100],
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <PhotoLibrary sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No images available
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  }

  return (
    <Box>
      {/* Main Carousel */}
      <Card sx={{ position: 'relative', height, overflow: 'hidden' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
            style={{ height: '100%' }}
          >
            <CardMedia
              component="img"
              image={images[currentIndex]}
              alt={`Store image ${currentIndex + 1}`}
              sx={{
                height: '100%',
                objectFit: 'cover',
                cursor: 'pointer',
              }}
              onClick={() => setDialogOpen(true)}
            />
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <IconButton
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
              onClick={prevImage}
            >
              <ChevronLeft />
            </IconButton>

            <IconButton
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
              onClick={nextImage}
            >
              <ChevronRight />
            </IconButton>
          </>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">
              {currentIndex + 1} / {images.length}
            </Typography>
          </Box>
        )}
      </Card>

      {/* Thumbnail Navigation */}
      {showThumbnails && images.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2, overflow: 'auto', pb: 1 }}>
          {images.map((image, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Card
                sx={{
                  minWidth: 80,
                  height: 60,
                  cursor: 'pointer',
                  border: index === currentIndex ? 2 : 1,
                  borderColor: index === currentIndex ? theme.palette.primary.main : theme.palette.divider,
                  overflow: 'hidden',
                }}
                onClick={() => goToImage(index)}
              >
                <CardMedia
                  component="img"
                  image={image}
                  alt={`Thumbnail ${index + 1}`}
                  sx={{
                    height: '100%',
                    objectFit: 'cover',
                    opacity: index === currentIndex ? 1 : 0.7,
                  }}
                />
              </Card>
            </motion.div>
          ))}
        </Box>
      )}

      {/* Full Screen Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              zIndex: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <Close />
          </IconButton>

          <Box sx={{ textAlign: 'center', backgroundColor: 'black', p: 2 }}>
            <img
              src={images[currentIndex]}
              alt={`Store image ${currentIndex + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
              }}
            />
          </Box>

          {/* Navigation in dialog */}
          {images.length > 1 && (
            <>
              <IconButton
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
                onClick={prevImage}
              >
                <ChevronLeft />
              </IconButton>

              <IconButton
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
                onClick={nextImage}
              >
                <ChevronRight />
              </IconButton>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', backgroundColor: 'black' }}>
          <Button onClick={() => setDialogOpen(false)} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageCarousel;
